/*
 * Copyright (c) 2023 Irian Software Development SRL. All Rights Reserved.
 * This software is the proprietary information of Irian Software Development SRL.
 * Use is subject to license and non-disclosure terms
 */

import { ModuleWithProviders, NgModule, Type } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AuthRoutingModule } from './auth-routing.module';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { StateStorageService } from './state-storage.service';
import { UsersService } from './users.service';
import { UsersComponent } from './users/users.component';
import { UserRightsComponent } from './user/user-rights/user-rights.component';
import { UserDetailsComponent } from './user/user-details/user-details.component';
import { AuthLogsComponent } from './auth-log/auth-logs/auth-logs.component';
import { AuthLogsService } from './auth-log/auth-logs.service';
import { TableModule } from 'primeng/table';
import { EnvironmentModule, SharedComponentsModule } from '@irian-ro/shared-components-module';
import * as langs from '../i18n/i18n';
import { UserInputComponent } from './user/user-input/user-input.component';
import { UserEditComponent } from './user/user-edit/user-edit.component';
import { SelectModule } from 'primeng/select';
import { MultiSelectModule } from 'primeng/multiselect';
import { ButtonModule } from 'primeng/button';
import { PasswordModule } from 'primeng/password';
import { InputTextModule } from 'primeng/inputtext';
import { CheckboxModule } from 'primeng/checkbox';
import { TooltipModule } from 'primeng/tooltip';
import { MessagesModule } from 'primeng/messages';
import { MessageModule } from 'primeng/message';
import { ToggleSwitch } from 'primeng/toggleswitch';
import { FirstUserFormComponent } from './user/first-user-form/first-user-form.component';
import { CardModule } from 'primeng/card';
import { RippleModule } from 'primeng/ripple';
import { RoleConfigurationViewComponent } from './roles/role-configuration-view/role-configuration-view.component';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { UserInputsAndRolesViewComponent } from './user-inputs-and-roles-view/user-inputs-and-roles-view.component';
import { UserWithRolesEditComponent } from './user-with-roles-edit/user-with-roles-edit.component';
import { AbstractRoleService } from './roles/abstract-role.service';
import { ROLE_SERVICE_TOKEN } from './roles/roles.config';
import { RoleService } from './roles/role.service';
import { AbstractSaveUserService } from './abstract-save-user.service';
import { USERS_SAVE_SERVICE_TOKEN } from './users.config';
import { UserFormComponent } from './user/user-form/user-form.component';
import { UserOtpCodeFormComponent } from './user/user-otp-code-form/user-otp-code-form.component';
import { UserChangePasswordComponent } from './user/user-change-password/user-change-password.component';
import { ToastModule } from 'primeng/toast';
import { FormInlineErrorDirective } from '@irian-ro/shared-ui';
import { provideHttpClient, withRequestsMadeViaParent } from '@angular/common/http';
import { OverridePasswordOverlayPositionDirective } from './user/user-input/override-password-overlay-position-direc.directive';
import { DividerModule } from 'primeng/divider';
import { PasswordRulesComponent } from './user/user-form/password-rules/password-rules.component';
import { SharedAuthLogsComponent } from './auth-log/shared-auth-logs/shared-auth-logs.component';
import { FloatLabel } from 'primeng/floatlabel';
import { TextareaModule } from 'primeng/textarea';

@NgModule({
  imports: [TextareaModule, 
    TableModule,
    SelectModule,
    MultiSelectModule,
    CommonModule,
    AuthRoutingModule,
    InputTextModule,
    PasswordModule,
    CheckboxModule,
    TooltipModule,
    ButtonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    MessagesModule,
    MessageModule,
    SharedComponentsModule,
    EnvironmentModule,
    ToggleSwitch,
    CardModule,
    RippleModule,
    ConfirmDialogModule,
    ToastModule,
    FormInlineErrorDirective,
    OverridePasswordOverlayPositionDirective,
    DividerModule,
    PasswordRulesComponent,
    SharedAuthLogsComponent,
    FloatLabel,
    ToggleSwitch
  ],
  declarations: [
    UsersComponent,
    UserRightsComponent,
    UserDetailsComponent,
    AuthLogsComponent,
    UserInputComponent,
    UserEditComponent,
    FirstUserFormComponent,
    RoleConfigurationViewComponent,
    UserInputsAndRolesViewComponent,
    UserWithRolesEditComponent,
    UserFormComponent,
    UserOtpCodeFormComponent,
    UserChangePasswordComponent
  ],
  exports: [
    UserRightsComponent,
    FirstUserFormComponent,
    UserInputComponent,
    RoleConfigurationViewComponent,
    UserInputsAndRolesViewComponent,
    UserWithRolesEditComponent,
    UserFormComponent,
    PasswordRulesComponent
  ],
  //TODO Remove provideHttpClient after FileUploadModule is fixed by primeng - it should not import HttpClientModule
  providers: [UsersService, AuthLogsService, StateStorageService, provideHttpClient(withRequestsMadeViaParent())],
})
export class AuthModule {
  constructor(translateService: TranslateService) {
    Object.keys(langs).map((lang) => {
      translateService.setTranslation(lang, langs[lang], true);
    });
  }

  static forRoot(config?: {
    roleService?: Type<AbstractRoleService>;
    userSaveService?: Type<AbstractSaveUserService>;
  }): ModuleWithProviders<AuthModule> {
    return {
      ngModule: AuthModule,
      providers: [
        {
          provide: ROLE_SERVICE_TOKEN,
          useExisting: config?.roleService || RoleService,
        },
        {
          provide: USERS_SAVE_SERVICE_TOKEN,
          useExisting: config?.userSaveService || UsersService,
        }
      ],
    };
  }
}
